import React, { useState, useEffect } from 'react';
import { listUsers } from '../controllers/usersController';

const formatPoints = (value) => {
  const points = Number(value) || 0;
  return `${points} pts`;
};

const getRankIcon = (rank) => {
  switch (rank) {
    case 1: return '🥇';
    case 2: return '🥈';
    case 3: return '🥉';
    case 4: return '🏅';
    case 5: return '🏅';
    default: return '🎮';
  }
};

const getRankColor = (rank) => {
  switch (rank) {
    case 1: return '#FFD700'; // Gold
    case 2: return '#C0C0C0'; // Silver
    case 3: return '#CD7F32'; // Bronze
    case 4: return '#00E5FF'; // Cyan
    case 5: return '#9B59FF'; // Purple
    default: return '#666666'; // Gray
  }
};

const getRankTitle = (rank) => {
  switch (rank) {
    case 1: return 'Gaming Legend';
    case 2: return 'Elite Player';
    case 3: return 'Pro Gamer';
    case 4: return 'Skilled Player';
    case 5: return 'Rising Star';
    default: return 'Gaming Enthusiast';
  }
};

const PlayerCard = ({ player, rank, isAnimated }) => {
  const rankColor = getRankColor(rank);
  const rankIcon = getRankIcon(rank);
  const rankTitle = getRankTitle(rank);
  
  return (
    <div 
      className={`player-card ${isAnimated ? 'animate-in' : ''}`}
      style={{
        background: `linear-gradient(135deg, ${rankColor}15, ${rankColor}05)`,
        border: `2px solid ${rankColor}40`,
        borderRadius: 'var(--border-radius-lg)',
        padding: '24px',
        marginBottom: '16px',
        position: 'relative',
        overflow: 'hidden',
        transition: 'all 0.3s ease',
        transform: isAnimated ? 'translateY(0)' : 'translateY(20px)',
        opacity: isAnimated ? 1 : 0,
        animationDelay: `${rank * 0.1}s`
      }}
      onMouseEnter={(e) => {
        e.target.style.transform = 'translateY(-4px)';
        e.target.style.boxShadow = `0 8px 32px ${rankColor}30`;
      }}
      onMouseLeave={(e) => {
        e.target.style.transform = 'translateY(0)';
        e.target.style.boxShadow = 'none';
      }}
    >
      {/* Rank Badge */}
      <div style={{
        position: 'absolute',
        top: '16px',
        right: '16px',
        background: rankColor,
        color: rank <= 3 ? '#000' : '#fff',
        borderRadius: '50%',
        width: '48px',
        height: '48px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '20px',
        fontWeight: 'bold',
        boxShadow: `0 4px 16px ${rankColor}50`
      }}>
        {rankIcon}
      </div>

      {/* Player Avatar */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        marginBottom: '16px'
      }}>
        <div style={{
          width: '64px',
          height: '64px',
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${rankColor}, ${rankColor}80)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '24px',
          fontWeight: 'bold',
          color: rank <= 3 ? '#000' : '#fff',
          boxShadow: `0 4px 16px ${rankColor}40`
        }}>
          {player.name.charAt(0).toUpperCase()}
        </div>
        
        <div style={{ flex: 1 }}>
          <h3 style={{
            margin: '0 0 4px 0',
            color: 'var(--text-primary)',
            fontSize: '1.3rem',
            fontWeight: '700'
          }}>
            {player.name}
          </h3>
          <p style={{
            margin: '0',
            color: rankColor,
            fontSize: '0.9rem',
            fontWeight: '600',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            {rankTitle}
          </p>
        </div>
      </div>

      {/* Stats Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '16px',
        marginBottom: '16px'
      }}>
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: 'var(--border-radius-sm)',
          padding: '12px',
          textAlign: 'center'
        }}>
          <div style={{
            color: 'var(--text-secondary)',
            fontSize: '0.8rem',
            fontWeight: '500',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginBottom: '4px'
          }}>
            Games Played
          </div>
          <div style={{
            color: 'var(--accent)',
            fontSize: '1.5rem',
            fontWeight: 'bold'
          }}>
            {player.total_units_played || 0}
          </div>
        </div>
        
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: 'var(--border-radius-sm)',
          padding: '12px',
          textAlign: 'center'
        }}>
          <div style={{
            color: 'var(--text-secondary)',
            fontSize: '0.8rem',
            fontWeight: '500',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginBottom: '4px'
          }}>
            Points
          </div>
          <div style={{
            color: rankColor,
            fontSize: '1.5rem',
            fontWeight: 'bold'
          }}>
            {formatPoints(player.total_amount_spent || 0)}
          </div>
        </div>
      </div>

      {/* Phone Number */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.03)',
        borderRadius: 'var(--border-radius-sm)',
        padding: '8px 12px',
        textAlign: 'center'
      }}>
        <span style={{
          color: 'var(--text-secondary)',
          fontSize: '0.9rem',
          fontFamily: 'monospace'
        }}>
          📱 {player.phone}
        </span>
      </div>

      {/* Decorative Elements */}
      <div style={{
        position: 'absolute',
        top: '-20px',
        right: '-20px',
        width: '80px',
        height: '80px',
        background: `radial-gradient(circle, ${rankColor}20, transparent)`,
        borderRadius: '50%',
        opacity: 0.3
      }} />
      
      <div style={{
        position: 'absolute',
        bottom: '-10px',
        left: '-10px',
        width: '60px',
        height: '60px',
        background: `radial-gradient(circle, ${rankColor}15, transparent)`,
        borderRadius: '50%',
        opacity: 0.2
      }} />
    </div>
  );
};

const AchievementBadge = ({ title, description, icon, color }) => (
  <div style={{
    background: `linear-gradient(135deg, ${color}20, ${color}10)`,
    border: `1px solid ${color}40`,
    borderRadius: 'var(--border-radius-sm)',
    padding: '12px 16px',
    textAlign: 'center',
    marginBottom: '12px'
  }}>
    <div style={{ fontSize: '24px', marginBottom: '8px' }}>{icon}</div>
    <div style={{
      color: 'var(--text-primary)',
      fontSize: '0.9rem',
      fontWeight: '600',
      marginBottom: '4px'
    }}>
      {title}
    </div>
    <div style={{
      color: 'var(--text-secondary)',
      fontSize: '0.8rem'
    }}>
      {description}
    </div>
  </div>
);

export default function TopPlayersLeaderboard() {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [animatedCards, setAnimatedCards] = useState(false);

  const fetchTopPlayers = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await listUsers({ 
        page: 1, 
        limit: 20, 
        sortBy: 'total_amount_spent', 
        sortOrder: 'desc' 
      });
      
      const users = Array.isArray(result.data) ? result.data : [];
      setPlayers(users);
      
      // Trigger animation after a short delay
      setTimeout(() => {
        setAnimatedCards(true);
      }, 100);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTopPlayers();
  }, []);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '80px 20px',
        textAlign: 'center'
      }}>
        <div style={{
          width: '60px',
          height: '60px',
          border: '4px solid var(--accent)',
          borderTop: '4px solid transparent',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          marginBottom: '24px'
        }} />
        <h3 style={{
          color: 'var(--text-primary)',
          fontSize: '1.2rem',
          fontWeight: '600',
          marginBottom: '8px'
        }}>
          Loading Champions...
        </h3>
        <p style={{
          color: 'var(--text-secondary)',
          fontSize: '0.9rem'
        }}>
          Fetching the top players from the leaderboard
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '80px 20px',
        textAlign: 'center'
      }}>
        <div style={{
          fontSize: '48px',
          marginBottom: '16px'
        }}>
          😞
        </div>
        <h3 style={{
          color: '#ff6363',
          fontSize: '1.2rem',
          fontWeight: '600',
          marginBottom: '8px'
        }}>
          Failed to Load Leaderboard
        </h3>
        <p style={{
          color: 'var(--text-secondary)',
          fontSize: '0.9rem',
          marginBottom: '16px'
        }}>
          {error}
        </p>
        <button
          onClick={fetchTopPlayers}
          style={{
            padding: '12px 24px',
            background: 'var(--accent)',
            color: '#0b1735',
            border: 'none',
            borderRadius: 'var(--border-radius-sm)',
            fontSize: '0.9rem',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          Try Again
        </button>
      </div>
    );
  }

  if (players.length === 0) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '80px 20px',
        textAlign: 'center'
      }}>
        <div style={{
          fontSize: '64px',
          marginBottom: '24px'
        }}>
          🎮
        </div>
        <h3 style={{
          color: 'var(--text-primary)',
          fontSize: '1.4rem',
          fontWeight: '600',
          marginBottom: '8px'
        }}>
          No Players Yet
        </h3>
        <p style={{
          color: 'var(--text-secondary)',
          fontSize: '1rem',
          maxWidth: '400px'
        }}>
          Be the first to start playing and climb to the top of the leaderboard!
        </p>
      </div>
    );
  }

  const top3Players = players.slice(0, 3);
  const remainingPlayers = players.slice(3);

  return (
    <div style={{ padding: '0 8px' }}>


      {/* Top 3 Podium */}
      {top3Players.length > 0 && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-end',
          gap: '20px',
          marginBottom: '40px',
          padding: '0 20px'
        }}>
          {/* 2nd Place */}
          {top3Players[1] && (
            <div style={{
              background: 'linear-gradient(135deg, #9B59FF, #8E44AD)',
              borderRadius: '20px 20px 0 0',
              padding: '24px 20px 20px',
              textAlign: 'center',
              position: 'relative',
              width: '140px',
              height: '180px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              boxShadow: '0 8px 32px rgba(155, 89, 255, 0.3)'
            }}>
              <div style={{
                position: 'absolute',
                top: '-20px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #9B59FF, #8E44AD)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '24px',
                fontWeight: 'bold',
                color: '#fff',
                boxShadow: '0 4px 16px rgba(155, 89, 255, 0.4)'
              }}>
                {top3Players[1].name.charAt(0).toUpperCase()}
              </div>
              
              <div style={{ marginTop: '20px' }}>
                <div style={{
                  color: '#fff',
                  fontSize: '1rem',
                  fontWeight: '600',
                  marginBottom: '4px'
                }}>
                  {top3Players[1].name}
                </div>
                <div style={{
                  color: '#fff',
                  fontSize: '0.9rem',
                  opacity: 0.9
                }}>
                  {formatPoints(top3Players[1].total_amount_spent || 0)}
                </div>
              </div>
              
              <div style={{
                color: '#fff',
                fontSize: '3rem',
                fontWeight: 'bold',
                textShadow: '0 2px 8px rgba(0,0,0,0.3)'
              }}>
                2
              </div>
            </div>
          )}

          {/* 1st Place */}
          {top3Players[0] && (
            <div style={{
              background: 'linear-gradient(135deg, #FFD700, #FFA500)',
              borderRadius: '20px 20px 0 0',
              padding: '32px 24px 24px',
              textAlign: 'center',
              position: 'relative',
              width: '160px',
              height: '220px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              boxShadow: '0 12px 40px rgba(255, 215, 0, 0.4)',
              transform: 'translateY(-10px)'
            }}>
              <div style={{
                position: 'absolute',
                top: '-25px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '70px',
                height: '70px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #FFD700, #FFA500)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '28px',
                fontWeight: 'bold',
                color: '#000',
                boxShadow: '0 6px 20px rgba(255, 215, 0, 0.5)'
              }}>
                {top3Players[0].name.charAt(0).toUpperCase()}
              </div>
              
              <div style={{ marginTop: '25px' }}>
                <div style={{
                  color: '#000',
                  fontSize: '1.1rem',
                  fontWeight: '700',
                  marginBottom: '4px'
                }}>
                  {top3Players[0].name}
                </div>
                <div style={{
                  color: '#000',
                  fontSize: '1rem',
                  opacity: 0.8,
                  fontWeight: '600'
                }}>
                  {formatPoints(top3Players[0].total_amount_spent || 0)}
                </div>
              </div>
              
              <div style={{
                color: '#000',
                fontSize: '4rem',
                fontWeight: 'bold',
                textShadow: '0 2px 8px rgba(0,0,0,0.2)'
              }}>
                1
              </div>
            </div>
          )}

          {/* 3rd Place */}
          {top3Players[2] && (
            <div style={{
              background: 'linear-gradient(135deg, #E74C3C, #C0392B)',
              borderRadius: '20px 20px 0 0',
              padding: '20px 16px 16px',
              textAlign: 'center',
              position: 'relative',
              width: '120px',
              height: '160px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              boxShadow: '0 6px 24px rgba(231, 76, 60, 0.3)'
            }}>
              <div style={{
                position: 'absolute',
                top: '-18px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '50px',
                height: '50px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #E74C3C, #C0392B)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px',
                fontWeight: 'bold',
                color: '#fff',
                boxShadow: '0 3px 12px rgba(231, 76, 60, 0.4)'
              }}>
                {top3Players[2].name.charAt(0).toUpperCase()}
              </div>
              
              <div style={{ marginTop: '15px' }}>
                <div style={{
                  color: '#fff',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  marginBottom: '4px'
                }}>
                  {top3Players[2].name}
                </div>
                <div style={{
                  color: '#fff',
                  fontSize: '0.8rem',
                  opacity: 0.9
                }}>
                  {formatPoints(top3Players[2].total_amount_spent || 0)}
                </div>
              </div>
              
              <div style={{
                color: '#fff',
                fontSize: '2.5rem',
                fontWeight: 'bold',
                textShadow: '0 2px 8px rgba(0,0,0,0.3)'
              }}>
                3
              </div>
            </div>
          )}
        </div>
      )}

      {/* Remaining Players List */}
      {remainingPlayers.length > 0 && (
        <div style={{
          background: 'var(--card-bg)',
          border: '1px solid var(--card-border)',
          borderRadius: 'var(--border-radius-lg)',
          overflow: 'hidden',
          boxShadow: 'var(--neon-glow)'
        }}>
          {remainingPlayers.map((player, index) => (
            <div
              key={player._id}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '16px 24px',
                borderBottom: index < remainingPlayers.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none',
                background: index % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(0, 229, 255, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = index % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent';
              }}
            >
              {/* Rank */}
              <div style={{
                color: 'var(--text-secondary)',
                fontSize: '1rem',
                fontWeight: '600',
                minWidth: '40px',
                marginRight: '16px'
              }}>
                {String(index + 4).padStart(2, '0')}
              </div>

              {/* Trend Indicator */}
              <div style={{
                marginRight: '16px',
                fontSize: '12px'
              }}>
                <span style={{ color: '#00E5FF' }}>▲</span>
              </div>

              {/* Avatar */}
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, var(--accent), var(--accent-2))',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '16px',
                fontWeight: 'bold',
                color: '#0b1735',
                marginRight: '16px',
                boxShadow: '0 2px 8px rgba(0, 229, 255, 0.3)'
              }}>
                {player.name.charAt(0).toUpperCase()}
              </div>

              {/* Player Name */}
              <div style={{
                flex: 1,
                color: 'var(--text-primary)',
                fontSize: '1rem',
                fontWeight: '500'
              }}>
                {player.name}
              </div>

              {/* Points */}
              <div style={{
                color: 'var(--accent)',
                fontSize: '1rem',
                fontWeight: '600'
              }}>
                {formatPoints(player.total_amount_spent || 0)}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Refresh Button */}
      <div style={{
        textAlign: 'center',
        marginTop: '40px'
      }}>
        <button
          onClick={fetchTopPlayers}
          style={{
            padding: '12px 32px',
            background: 'var(--accent)',
            color: '#0b1735',
            border: 'none',
            borderRadius: 'var(--border-radius-sm)',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = '0 8px 24px rgba(0, 229, 255, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = 'none';
          }}
        >
          🔄 Refresh Leaderboard
        </button>
      </div>

      {/* CSS Animation */}
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .player-card {
          animation: slideInUp 0.6s ease forwards;
        }
        
        @keyframes slideInUp {
          from {
            transform: translateY(30px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
}
